import { useState } from "react"
import { Div, Text } from "atomize"
import PlayerTag from "../atoms/PlayerTag"
import { theme } from "../settings/config"
import { getPlayerFullName, getPtrLevel } from "../utils/func.utils"
import { MISSING_GROUP_LEVEL } from "../utils/substituteSuggestions.utils"

// quante proposte per elenco si vedono prima di "Altri…"
const SHOWN = { recovering: 4, others: 3 }

const recoveriesText = count => `${count} da recuperare`

// I nomi cominciano dove comincia il testo nel campo che hanno sopra: il suo
// rientro più il suo bordo (vedi TextInput)
const INSET = "calc(1rem + 3px)"

function SectionTitle({ children }) {
	return (
		<Text
			textSize="caption"
			textColor={theme.colors.text_light}
			m={{ t: "1rem", b: "0.4rem" }}
			p={{ x: INSET }}>
			{children}
		</Text>
	)
}

// Un elenco di proposte in un blocco bianco, come il campo per scrivere che
// ha sopra: sul fondo caldo del foglio sono la stessa famiglia. Una riga per
// allievo, tutta da toccare; i fili tra le righe sono il fondo del foglio.
function SuggestionsBlock({ title, suggestions, shown, onPick }) {
	const [isExpanded, setExpanded] = useState(false)
	if (suggestions.length === 0) return null

	const visible = isExpanded ? suggestions : suggestions.slice(0, shown)
	const hidden = suggestions.length - visible.length
	const line = `1px solid ${theme.colors.background}`

	return (
		<>
			<SectionTitle>{title}</SectionTitle>
			<Div bg={theme.colors.surface} rounded="15px" overflow="hidden">
				{visible.map(({ player, level, recoveries }, i) => {
					const ptrLevel = getPtrLevel(level)
					return (
						<Div
							key={player.playerID}
							tag="button"
							type="button"
							w="100%"
							minH="3.1rem"
							p={{ x: INSET, y: "0.5rem" }}
							d="flex"
							align="center"
							bg="transparent"
							cursor="pointer"
							style={{
								gap: "0.5rem",
								border: 0,
								borderTop: i === 0 ? 0 : line,
								textAlign: "left",
								font: "inherit",
							}}
							onClick={() => onPick(player)}>
							<Text textWeight="bold" textColor={theme.colors.gunmetal}>
								{getPlayerFullName(player)}
							</Text>
							{ptrLevel && (
								<PlayerTag
									text={ptrLevel.label}
									title={`Livello PTR ${ptrLevel.label}`}
									bg={theme.colors.gunmetal_lighter}
									textColor={theme.colors.gunmetal}
								/>
							)}
							{recoveries > 0 && (
								<Text
									textSize="caption"
									textWeight="500"
									textColor={theme.colors.primary}
									m={{ l: "auto" }}
									style={{ whiteSpace: "nowrap" }}>
									{recoveriesText(recoveries)}
								</Text>
							)}
						</Div>
					)
				})}
				{hidden > 0 && (
					<Div
						tag="button"
						type="button"
						w="100%"
						h="2.9rem"
						p={{ x: INSET }}
						d="flex"
						align="center"
						bg="transparent"
						cursor="pointer"
						textColor={theme.colors.text_light}
						style={{ border: 0, borderTop: line, font: "inherit" }}
						onClick={() => setExpanded(true)}>
						{hidden === 1 ? "Altro 1 allievo" : `Altri ${hidden} allievi`}
					</Div>
				)}
			</Div>
		</>
	)
}

// le parole dei moduli dove il livello si segna
const formWord = text => (
	<Text tag="span" textWeight="600" textColor={theme.colors.gunmetal}>
		{text}
	</Text>
)

// Senza livelli chi pagherebbe la volta non si può proporre: al posto del
// suo elenco, con lo stesso titolo, c'è cosa segnare e dove, con le parole
// dei moduli. Si vede che l'elenco esiste, e cosa serve per riempirlo. Il
// livello del corso si segna solo in un CAD; altrove conta quello di chi
// manca.
function LevelHint({ missingLevel, canSetGroupLevel, courseInSentence, absentName }) {
	const hint =
		missingLevel !== MISSING_GROUP_LEVEL ? (
			<>
				Nessuno degli allievi liberi ha un livello: segnalo nella loro{" "}
				{formWord("Scheda tecnica")}, o il {formWord("Livello PTR")} dei loro corsi, e
				qui vedrai chi gioca al livello del gruppo.
			</>
		) : canSetGroupLevel ? (
			<>
				Segna il {formWord("Livello PTR")} {courseInSentence}, da Modifica, o degli allievi
				nella loro {formWord("Scheda tecnica")}: qui vedrai anche chi gioca al livello
				del gruppo.
			</>
		) : (
			<>
				Segna il {formWord("Livello PTR")} di {absentName} nella sua{" "}
				{formWord("Scheda tecnica")}: qui vedrai anche chi gioca al suo livello.
			</>
		)

	return (
		<>
			<SectionTitle>Pagherebbero la volta</SectionTitle>
			<Div bg={theme.colors.surface} rounded="15px" p={{ x: INSET, y: "0.75rem" }}>
				<Text
					textSize="caption"
					textColor={theme.colors.text_light}
					style={{ lineHeight: 1.45 }}>
					{hint}
				</Text>
			</Div>
		</>
	)
}

// Chi potrebbe venire al posto dell'assente: in cima chi ha recuperi da fare
// di questo corso, sotto chi pagherebbe la volta (vedi
// substituteSuggestions.utils). Toccarne uno è come scriverne il nome.
function SubstituteSuggestions({
	suggestions,
	onPick,
	courseInSentence,
	canSetGroupLevel,
	absentName,
}) {
	if (!suggestions) return null
	return (
		<>
			<SuggestionsBlock
				title={`Hanno un recupero ${courseInSentence}`}
				suggestions={suggestions.recovering}
				shown={SHOWN.recovering}
				onPick={onPick}
			/>
			{suggestions.missingLevel ? (
				<LevelHint
					missingLevel={suggestions.missingLevel}
					canSetGroupLevel={canSetGroupLevel}
					courseInSentence={courseInSentence}
					absentName={absentName}
				/>
			) : (
				<SuggestionsBlock
					title="Pagherebbero la volta"
					suggestions={suggestions.others}
					shown={SHOWN.others}
					onPick={onPick}
				/>
			)}
		</>
	)
}

export default SubstituteSuggestions
